import { useCallback, useSyncExternalStore } from 'react';

export type AuthUser = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  tenantId?: string;
  photo?: { path?: string | null } | null;
};

export type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  token: string | null;
  login: (user?: AuthUser) => void;
  logout: () => void;
  setUser: (user: AuthUser | null) => void;
};

const TOKEN_KEY = 'mks_forms_token';
const USER_KEY = 'mks_forms_user';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as AuthUser) : null;
  } catch {
    return null;
  }
}

function login(user?: AuthUser): void {
  if (user) {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
      /* storage indisponível — mantém só em memória */
    }
  }
  setState({
    user: user ?? state.user,
    isAuthenticated: true,
    token: readToken(),
  });
}

function logout(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* noop */
  }
  setState({ user: null, isAuthenticated: false, token: null });
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

function setUser(user: AuthUser | null): void {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* noop */
  }
  setState({ user });
}

let state: AuthState = {
  user: readStoredUser(),
  isAuthenticated: readToken() !== null,
  token: readToken(),
  login,
  logout,
  setUser,
};

const listeners = new Set<() => void>();

function setState(patch: Partial<AuthState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useAuthStore(): AuthState;
export function useAuthStore<T>(selector: (s: AuthState) => T): T;
export function useAuthStore<T>(selector?: (s: AuthState) => T): AuthState | T {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
  if (!selector) return snapshot;
  return selector(snapshot);
}

export const getAuthStore = (): AuthState => state;

export const useAuthUser = (): AuthUser | null =>
  useSyncExternalStore(
    subscribe,
    () => state.user,
    () => null,
  );

export const useLogout = () => useCallback(() => state.logout(), []);
